

import { getZodiacElement, getToneTheme } from '../utils/shareCardHelpers';

export interface PersonalProfileData {
  nickname?: string | null;
  city?: string | null;
  height?: number | string | null;
  weight?: number | string | null;
  bodyType?: string | null;
  job?: string | null;
  role?: string[] | string | null;
  zodiac?: { name?: string; symbol?: string } | null;
  rel?: string | null;
  guTitle?: string | null;
  guHook?: string | null;
  avatarUrl?: string | null;
}

interface PersonalProfileCardProps {
  data?: PersonalProfileData;
  displayName?: string;
  totalLikes?: number;
  isLiked?: boolean;
  onDoubleTap?: () => void;
  // When rendered inside a fixed aspect wrapper (like the swipe), we fill it
}

function shortRel(rel?: string | null): string {
  if (!rel) return 'Tìm hiểu chân thành';
  const r = rel.toLowerCase();
  if (r.includes('1-1') || r.includes('truyền thống')) return 'Chung thủy 1-1';
  if (r.includes('cởi mở')) return 'Cởi mở • Cam kết tình cảm';
  if (r.includes('chậm rãi') || r.includes('tự nhiên')) return 'Tìm hiểu chậm rãi';
  if (r.includes('tri kỷ') || r.includes('đồng hành')) return 'Bạn đời tri kỷ';
  return 'Quan hệ chân thành';
}

function formatPhysical(height?: number | string | null, weight?: number | string | null, bodyType?: string | null) {
  const h = height != null ? `${height}cm` : '';
  const w = weight != null ? `${weight}kg` : '';
  let b = '';
  if (bodyType) {
    b = bodyType.split('/')[0]?.trim() || bodyType;
  }
  return [h, w, b].filter(Boolean).join(' • ');
}

function getRoleBadges(role?: string[] | string | null): string[] {
  if (!role) return [];
  if (Array.isArray(role)) return role.filter(Boolean);
  if (typeof role === 'string') {
    try {
      const parsed = JSON.parse(role);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch {}
    return role.split(/[,/]/).map(s => s.trim()).filter(Boolean);
  }
  return [];
}

function getInitial(name?: string | null) {
  return (name || 'U').trim().charAt(0).toUpperCase() || 'U';
}

function getAvatarEmoji(roleBadges: string[]) {
  const r = (roleBadges[0] || '').toLowerCase();
  if (r.includes('top')) return '💪';
  if (r.includes('bottom')) return '🔥';
  if (r.includes('vers')) return '⚖️';
  return '👤';
}

export default function PersonalProfileCard({
  data = {},
  displayName,
  totalLikes = 0,
  isLiked = false,
  onDoubleTap,
}: PersonalProfileCardProps) {
  const nick = displayName || data.nickname || 'Ẩn danh';
  const city = data.city || '';
  const physical = formatPhysical(data.height, data.weight, data.bodyType);
  const job = data.job || '';
  const roleBadges = getRoleBadges(data.role);
  const roleDisplay = roleBadges.length ? roleBadges.join(' / ') : 'Trao đổi sau';
  const zodiac = data.zodiac;
  const relShort = shortRel(data.rel);
  const guTitle = data.guTitle || 'Gu khi yêu';
  const guHook = data.guHook || 'Tìm kiếm sự kết nối chân thật.';

  const initial = getInitial(nick);
  const avatarEmoji = getAvatarEmoji(roleBadges);

  // Nice gradient for default avatar (subtle, premium feel)
  const avatarGradient = 'linear-gradient(135deg, #1f2937 0%, #111827 50%, #1f2937 100%)';

  const hasRealAvatar = data.avatarUrl && data.avatarUrl !== '[]' && !data.avatarUrl.startsWith('[');

  const handleDouble = () => {
    if (onDoubleTap) onDoubleTap();
  };

  // Color tone based on zodiac element (fire/air/earth/water) for profile card
  const zodiacEl = zodiac?.name ? getZodiacElement(zodiac.name) : null;
  const profileHeat = zodiacEl ? { fire: 0.85, air: 0.5, earth: 0.4, water: 0.15 }[zodiacEl] || 0.5 : 0.5;
  const profileTone = getToneTheme(profileHeat);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        background: profileTone.gradient,
        border: `3px solid ${profileTone.border}`,
        borderRadius: 18,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        color: '#f1f5f9',
        fontFamily: "'Be Vietnam Pro', system-ui, -apple-system, sans-serif",
        position: 'relative',
        boxShadow: '0 12px 40px rgba(0,0,0,0.55)',
      }}
      onDoubleClick={handleDouble}
    >
      {profileTone.glow && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: profileTone.glow,
            pointerEvents: 'none',
            borderRadius: 18,
            zIndex: 0,
          }}
        />
      )}
      {/* Avatar + Name area (old design kept) */}
      <div style={{ padding: '18px 18px 8px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
        <div
          style={{
            width: 132,
            height: 132,
            borderRadius: '50%',
            overflow: 'hidden',
            border: `3px solid ${profileTone.border}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            background: hasRealAvatar ? '#111' : avatarGradient,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 52,
            position: 'relative',
          }}
        >
          {hasRealAvatar ? (
            <img
              src={data.avatarUrl!}
              alt={nick}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <>
              <span style={{ fontSize: 64, fontWeight: 700, color: '#64748b', lineHeight: 1 }}>{initial}</span>
              <span style={{ position: 'absolute', bottom: 8, right: 8, fontSize: 28 }}>{avatarEmoji}</span>
            </>
          )}
        </div>

        <div style={{ marginTop: 12, fontSize: 22, fontWeight: 700, letterSpacing: '-0.3px' }}>{nick}</div>

        {zodiac && (
          <div style={{ marginTop: 2, fontSize: 13, color: '#94a3b8', display: 'flex', alignItems: 'center', gap: 4 }}>
            {zodiac.symbol ? <span>{zodiac.symbol}</span> : null}
            <span>{zodiac.name}</span>
          </div>
        )}

        {/* Role badges - very prominent (old design) */}
        <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
          {roleBadges.length > 0 ? (
            roleBadges.map((r, i) => (
              <span
                key={i}
                style={{
                  background: `${profileTone.accent}26`,
                  color: profileTone.accent,
                  fontSize: 12,
                  fontWeight: 700,
                  padding: '2px 10px',
                  borderRadius: 999,
                  border: '1px solid rgba(212,175,55,0.35)',
                  letterSpacing: '0.5px',
                }}
              >
                {r}
              </span>
            ))
          ) : (
            <span style={{ color: '#64748b', fontSize: 12 }}>{roleDisplay}</span>
          )}
        </div>
      </div>

      {/* Quick facts (old design kept) */}
      <div style={{ padding: '0 18px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        {city && (
          <div style={{ fontSize: 13, color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: 6 }}>
            📍 <span>{city}</span>
          </div>
        )}

        {physical && (
          <div style={{ fontSize: 13, color: '#94a3b8' }}>
            📏 {physical}
          </div>
        )}

        {job && (
          <div style={{ fontSize: 13, color: '#94a3b8' }}>
            💼 {job}
          </div>
        )}
      </div>

      {/* Gu section (old design kept, with similar surface) */}
      <div style={{ margin: '6px 16px 0', background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '10px 12px', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ fontSize: 10, color: profileTone.accent, fontWeight: 700, letterSpacing: '0.6px', marginBottom: 2 }}>
          GU KHI YÊU
        </div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#f1f5f9', lineHeight: 1.25 }}>
          {guTitle}
        </div>
        {guHook && (
          <div style={{ fontSize: 11, color: '#64748b', marginTop: 3, fontStyle: 'italic', lineHeight: 1.3 }}>
            “{guHook.length > 68 ? guHook.slice(0, 65) + '...' : guHook}”
          </div>
        )}
      </div>

      {/* Tìm kiếm (old design kept) */}
      <div style={{ margin: '8px 16px 0', background: 'rgba(103,232,249,0.06)', borderRadius: 10, padding: '8px 12px', border: '1px solid rgba(103,232,249,0.15)' }}>
        <div style={{ fontSize: 10, color: profileTone.accent, fontWeight: 700, letterSpacing: '0.6px', marginBottom: 1 }}>
          TÌM KIẾM
        </div>
        <div style={{ fontSize: 13, color: '#e0f2fe', lineHeight: 1.3 }}>
          {relShort}
        </div>
      </div>

      {/* Bottom bar (old design kept) */}
      <div style={{ marginTop: 'auto', padding: '10px 16px 12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: '#475569', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {isLiked ? '💛' : '❤️'} <span style={{ color: profileTone.accent }}>{totalLikes || '—'}</span>
        </div>
        <div style={{ fontSize: 10, opacity: 0.6 }}>Kéo ngang xem quiz →</div>
      </div>
    </div>
  );
}
